import React, { useState, useEffect } from 'react'
import { useLocation, useHistory, Route } from 'react-router-dom'
import { getTheme, callAPI, viewportHeight } from 'helpers'
import qs from 'qs'
import { get } from 'lodash'

// Containers
import ExamDetail from 'containers/ExamDetail'

// Components
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'
import GridContainer from 'components/GridContainer'
import Spinner from 'components/Spinner'
import Heading from 'components/Heading'
import ContentWrapper from 'components/ContentWrapper'
import CustomPopconfirm from 'components/CustomPopconfirm'
import { Button, Table, Empty, Icon } from 'antd'

const LIMIT = 20

export default function () {
  const location = useLocation()
  const history = useHistory()
  const [examCount, setExamCount] = useState(0)
  const [exams, setExams] = useState([])
  const [loading, setLoading] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [hasMore, setHasMore] = useState(true)

  const currentPage = parseInt(get(qs.parse(location.search.slice(1)), 'page') || 1, 10)
  const offset = (currentPage - 1) * LIMIT

  useEffect(() => {
    const initialFetch = async () => {
      try {
        setLoading(true)
        const [{ result: _examCount }, { result: _exams }] = await Promise.all([
          callAPI({ url: '/exams/count' }),
          callAPI({ url: '/exams', query: { offset, limit: LIMIT } })
        ])
        setLoading(false)
        setExamCount(_examCount)
        setExams(_exams)
        setHasMore(_exams.length === LIMIT)

        if (offset > 0 && _examCount && !_exams.length) { history.replace('/exams') }
      } catch (error) {
        console.error('Error while trying to initial fetch.', error)
        setLoading(false)
        setExams([])
      }
    }

    initialFetch()
  }, [])

  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true)
        const { result: _exams } = await callAPI({
          url: '/exams',
          query: { offset, limit: LIMIT }
        })
        setLoading(false)
        setExams(_exams)
      } catch (error) {
        console.error('Error while trying to fetch exams.', error)
        setLoading(false)
        setExams([])
      }
    }

    fetchExams()
  }, [offset])

  const _onLoadMore = async () => {
    if (loading || !hasMore) return
    try {
      setLoading(true)
      const { result: _exams } = await callAPI({
        url: '/exams',
        query: { offset: exams.length, limit: LIMIT }
      })
      setLoading(false)
      setExams(_exams)
      setHasMore(_exams.length === LIMIT)
    } catch (error) {
      console.error('Error while trying to load more.', error)
      setLoading(false)
      setExams([])
    }
  }

  const _onExamCreate = () =>
    history.push('/exams/create')

  const _onExamRemove = async (examId) => {
    try {
      setRemoving(examId)
      await callAPI({
        method: 'DELETE',
        url: `/exams/${examId}`
      })
      setExamCount(_examCount => _examCount - 1)
      setExams(_exams => _exams.filter(_exam => _exam.classExamId !== examId))
      setRemoving(null)
    } catch (error) {
      console.error('Error while trying to remove a class exam and its questions.', error)
      setRemoving(null)
    }
  }

  const columns = [
    { key: 'classExamId', title: 'รหัสแบบทดสอบ', align: 'center', render: ({ classExamId }) => classExamId },
    { key: 'title', title: 'ชื่อแบบทดสอบ', render: ({ title }) => title },
    { key: 'courseName', title: 'ชื่อคอร์สเรียน', render: ({ courseName }) => courseName },
    { key: 'questionCount', title: 'จำนวนคำถาม', align: 'center', render: ({ questionCount }) => questionCount || 0 },
    { key: 'buttons',
      title: '',
      render: ({ classExamId, title }) => (
        <div>
          <Button style={{ marginRight: 10 }}
            icon='edit'
            onClick={() => history.push(`/exams/${classExamId}`)} />
          <CustomPopconfirm
            title={`ยืนยันที่จะลบแบบทดสอบ "${title}" ?`}
            icon={<Icon type='question-circle-o' />}
            onConfirm={() => _onExamRemove(classExamId)}>
            <Button type='danger'
              icon='delete'
              loading={classExamId === removing}
              disabled={classExamId === removing} />
          </CustomPopconfirm>
        </div>
      ) }
  ]

  return (
    <section>
      {location.pathname === '/exams' && (
        <GridContainer>
          <Heading
            title={`แบบทดสอบ ${examCount ? `(${examCount})` : ''}`}
            buttonProps={{
              onClick: _onExamCreate
            }}
            buttonTitle='เพิ่มแบบทดสอบ' />
          {loading ? (
            <ContentWrapper><Spinner /></ContentWrapper>
          ) : exams.length ? (
            <ContentWrapper style={{ padding: 0 }}>
              <Desktop>
                <Table
                  rowKey={({ classExamId }) => classExamId}
                  columns={columns}
                  dataSource={exams.slice(0, LIMIT)}
                  pagination={{
                    current: currentPage,
                    pageSize: LIMIT,
                    total: examCount,
                    onChange: (page, pageSize) => history.push(`/exams?page=${page}`)
                  }} />
              </Desktop>
              <Mobile>
                <InfiniteScroll
                  initialLoad={false}
                  threshold={viewportHeight() / 3}
                  loadMore={_onLoadMore}
                  hasMore={hasMore}>
                  {exams.map(_exam =>
                    <ExamItem
                      key={_exam.classExamId}
                      exam={_exam}
                      onEdit={() => history.push(`/exams/${_exam.classExamId}`)}
                      onRemove={() => _onExamRemove(_exam.classExamId)}
                      removing={removing === _exam.classExamId} />
                  )}
                </InfiniteScroll>
              </Mobile>
            </ContentWrapper>
          ) : (
            <ContentWrapper>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span>ยังไม่มีข้อมูลแบบทดสอบ</span>}>
                <Button type='primary' icon='plus' onClick={_onExamCreate}>เพิ่มแบบทดสอบแรกของคุณ</Button>
              </Empty>
            </ContentWrapper>
          )}
        </GridContainer>
      )}
      <Route exact path='/exams/:examId' component={ExamDetail} />
    </section>
  )
}

const Desktop = styled.div`
  @media(max-width: ${getTheme`media.mobile`}) { display: none; }
`
const Mobile = styled.div`
  @media(min-width: ${getTheme`media.desktop`}) { display: none; }
`

const ExamItem = ({ onEdit, onRemove, removing, exam: { classExamId, title, courseName, questionCount } }) => {
  return (
    <ExamItemWrapper>
      <ExamItemRow>
        <ExamItemLabel>รหัสแบบทดสอบ</ExamItemLabel>
        <ExamItemValue>{classExamId}</ExamItemValue>
      </ExamItemRow>
      <ExamItemRow>
        <ExamItemLabel>ชื่อแบบทดสอบ</ExamItemLabel>
        <ExamItemValue>{title}</ExamItemValue>
      </ExamItemRow>
      <ExamItemRow>
        <ExamItemLabel>ชื่อคอร์สเรียน</ExamItemLabel>
        <ExamItemValue>{courseName}</ExamItemValue>
      </ExamItemRow>
      <ExamItemRow>
        <ExamItemLabel>จำนวนคำถาม</ExamItemLabel>
        <ExamItemValue>{questionCount}</ExamItemValue>
      </ExamItemRow>
      <div style={{ marginTop: 15 }}>
        <Button
          style={{ width: 'calc(50% - 5px)', maxWidth: 160, marginRight: 10 }}
          icon='edit'
          onClick={onEdit}>
          <span>แก้ไขแบบทดสอบ</span>
        </Button>
        <CustomPopconfirm
          title={`ยืนยันที่จะลบแบบทดสอบ "${title}" ?`}
          icon={<Icon type='question-circle-o' />}
          onConfirm={onRemove}>
          <Button
            style={{ width: 'calc(50% - 5px)', maxWidth: 160 }}
            type='danger'
            icon='delete'
            loading={removing}
            disabled={removing}>
            <span>ลบแบบทดสอบ</span>
          </Button>
        </CustomPopconfirm>
      </div>
    </ExamItemWrapper>
  )
}
const ExamItemWrapper = styled.div`
  padding: 15px;
  border-bottom: 1px solid ${getTheme`colors.border`};
  background-color: white;
`
const ExamItemRow = styled.div`
  line-height: 30px;
`
const ExamItemLabel = styled.div`
  display: inline-block;
  vertical-align: top;
  color: black;
  font-weight: 700;
  width: 50%;
`
const ExamItemValue = styled.div`
  display: inline-block;
  vertical-align: top;
  width: 50%;
`



// WEBPACK FOOTER //
// ./src/containers/Exams.js